/**
 * FIFINE MX - Scroll reveal
 * ---------------------------------------------------------------------------
 * Es la unica pieza del sistema de movimiento que Horizon no trae de fabrica.
 * Los hover de tarjeta, la barra sticky de compra y los tokens de easing ya
 * existen en el tema (assets/base.css y assets/sticky-add-to-cart.js); aqui
 * no se duplican.
 *
 * Reglas que aplica:
 *   1. El contenido es visible por defecto. La clase que oculta se agrega
 *      desde aqui, asi que sin JS -o con JS roto- no desaparece nada.
 *   2. No corre si el usuario pidio menos movimiento, ni si falta IO.
 *   3. Solo anima transform y opacity: cero reflow, cero CLS.
 *   4. threshold 0 + rootMargin. Un threshold de 0.15 deja invisible para
 *      siempre cualquier elemento mas alto que ~6.7x el viewport, porque su
 *      intersectionRatio nunca llega a 0.15.
 *   5. El escalonado se calcula por lote de entrada, no por nth-child: asi
 *      la segunda fila de una reja no arrastra el retraso acumulado de la
 *      primera.
 *   6. unobserve tras el primer disparo: no se re-anima al subir.
 *
 * Se carga como type="module", asi que aunque varias secciones pidan el
 * script, el navegador lo ejecuta una sola vez por URL.
 */

const SELECTOR = '[data-fif-reveal]';
const READY = 'fif-reveal-ready';
const IN = 'fif-reveal-in';
const STAGGER_MS = 80;
const STAGGER_MAX = 5; // 6 elementos escalonados (0..5), el resto entra junto

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {IntersectionObserver | null} */
let observer = null;

/** @type {WeakSet<Element>} */
const tracked = new WeakSet();

function supported() {
  return 'IntersectionObserver' in window && !reduceMotion.matches;
}

function getObserver() {
  if (observer) return observer;

  observer = new IntersectionObserver(
    (entries, io) => {
      // Solo las que entran, en orden de aparicion en el documento, para que
      // el escalonado siga la lectura y no el orden en que dispara el IO.
      const entering = entries.filter((entry) => entry.isIntersecting);
      if (entering.length === 0) return;

      entering.sort((a, b) => {
        const pos = a.target.compareDocumentPosition(b.target);
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });

      entering.forEach((entry, index) => {
        const el = /** @type {HTMLElement} */ (entry.target);
        const delay = Math.min(index, STAGGER_MAX) * STAGGER_MS;
        if (delay > 0) el.style.setProperty('--fif-reveal-delay', `${delay}ms`);
        el.classList.add(IN);
        io.unobserve(el);
      });
    },
    { threshold: 0, rootMargin: '0px 0px -8% 0px' }
  );

  return observer;
}

/**
 * @param {ParentNode} [root]
 */
function reveal(root) {
  if (!supported()) return;

  const scope = root || document;
  const targets = scope.querySelectorAll(SELECTOR);
  if (targets.length === 0) return;

  const io = getObserver();

  targets.forEach((el) => {
    if (tracked.has(el)) return;
    tracked.add(el);

    // Above the fold no se anima: ya esta a la vista cuando carga la pagina,
    // asi que ocultarlo produciria un parpadeo, no una entrada.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9) return;

    el.classList.add(READY);
    io.observe(el);
  });
}

/**
 * Si el usuario activa "reducir movimiento" con la pagina abierta, se limpia
 * todo lo que quedo preparado en vez de dejarlo en opacity 0.
 */
function releaseAll() {
  document.querySelectorAll(`.${READY}`).forEach((el) => {
    el.classList.remove(READY, IN);
  });
  observer?.disconnect();
  observer = null;
}

if (reduceMotion.addEventListener) {
  reduceMotion.addEventListener('change', (event) => {
    if (event.matches) releaseAll();
    else reveal();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => reveal(), { once: true });
} else {
  reveal();
}

// El theme editor monta y remonta secciones sin recargar la pagina.
document.addEventListener('shopify:section:load', (event) => {
  reveal(/** @type {any} */ (event).target);
});
